/*
 * The help content, in Bengali, ported from html-markup/assets/js/documentation.js
 * — which is itself the app/docs/* routes gathered onto one page.
 *
 * DATA, NOT MARKUP. The source authored bold and lists as HTML strings and the
 * Q&A answers as whole HTML fragments. Rendering those in React would mean
 * dangerouslySetInnerHTML on every paragraph, so inline emphasis is now a
 * marker the renderer understands (**bold**, _italic_, \`code\`) and every
 * answer is the same block array the rest of the file uses. Nothing here is
 * interpreted as HTML.
 *
 * Block types: p · steps · list · note (info|warning|danger|success) · qa · table
 *
 * The content is deliberately NOT translated or rewritten. It is the team's own
 * documentation, and the people who read it read Bengali.
 */

export const QUICK_START = [
    {
        "n": 1,
        "title": "অ্যাকাউন্ট তৈরি করুন",
        "text": "প্রতিষ্ঠানের নাম ও একটি identifier দিয়ে রেজিস্টার করুন। identifier পরে বদলানো যায় না।",
        "href": "/register",
        "cta": "রেজিস্টার পেজ"
    },
    {
        "n": 2,
        "title": "ডেস্কটপ অ্যাপ ইনস্টল করুন",
        "text": "প্রতিটি কর্মীর কম্পিউটারে অ্যাপ ইনস্টল করে ওই identifier দিয়ে সাইন ইন করান।",
        "href": "#topic-desktop-app-installation",
        "cta": "ইনস্টলেশন গাইড"
    },
    {
        "n": 3,
        "title": "সেটিংস ঠিক করুন",
        "text": "স্ক্রিনশট ইন্টারভাল, idle threshold, কাজের সময়সূচি ও productivity rule সেট করুন।",
        "href": "/organization",
        "cta": "সেটিংস"
    },
    {
        "n": 4,
        "title": "মনিটরিং শুরু",
        "text": "ড্যাশবোর্ডে কে কাজ করছে, কত সময় হয়েছে আর কী ব্যবহার হচ্ছে দেখুন।",
        "href": "/dashboard",
        "cta": "ড্যাশবোর্ড"
    }
];

export const FEATURES = [
    {
        "title": "কর্মচারী মনিটরিং",
        "text": "কাজের সময়, উৎপাদনশীলতা ও স্ক্রিনশট এক জায়গায়।",
        "tone": "bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300",
        "icon": "userRound"
    },
    {
        "title": "টিম ম্যানেজমেন্ট",
        "text": "কর্মীদের দলে ভাগ করে দলভিত্তিক রিপোর্ট নিন।",
        "tone": "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400",
        "icon": "team"
    },
    {
        "title": "সময় ট্র্যাকিং",
        "text": "কাজের সময়, বিরতি ও নিষ্ক্রিয় সময় স্বয়ংক্রিয়ভাবে।",
        "tone": "bg-violet-50 text-violet-600 dark:bg-violet-950/60 dark:text-violet-300",
        "icon": "clock"
    },
    {
        "title": "স্ক্রিনশট ক্যাপচার",
        "text": "নির্দিষ্ট বিরতিতে স্ক্রিনশট, প্রতিটি মনিটরের জন্য আলাদা।",
        "tone": "bg-orange-50 text-orange-600 dark:bg-orange-950/60 dark:text-orange-300",
        "icon": "camera"
    },
    {
        "title": "রিপোর্ট ও বিশ্লেষণ",
        "text": "দশ ধরনের রিপোর্ট, PDF ও Excel এক্সপোর্ট সহ।",
        "tone": "bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400",
        "icon": "chart"
    },
    {
        "title": "কাস্টমাইজেশন",
        "text": "প্রতিষ্ঠান ও প্রতি-কর্মী আলাদা সেটিংস।",
        "tone": "bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-300",
        "icon": "cog"
    }
];

export const TOPICS = [
    {
        "key": "user-guide",
        "title": "ব্যবহারকারী গাইড",
        "blurb": "ভূমিকা অনুযায়ী কে কী দেখতে পান এবং কোন পেজ কী কাজে লাগে।",
        "blocks": [
            {
                "type": "p",
                "text": "সিস্টেমে চারটি ভূমিকা আছে। কে কী করতে পারেন তা ভূমিকা দিয়েই ঠিক হয়।"
            },
            {
                "type": "table",
                "columns": [
                    "ভূমিকা",
                    "কী দেখতে পান",
                    "কী করতে পারেন"
                ],
                "rows": [
                    [
                        "**Super Admin**",
                        "সব প্রতিষ্ঠান",
                        "প্রতিষ্ঠান তৈরি, প্যাকেজ ও entitlement নিয়ন্ত্রণ"
                    ],
                    [
                        "**Admin**",
                        "নিজের প্রতিষ্ঠানের সব ডেটা",
                        "কর্মী, টিম, প্রজেক্ট, সেটিংস, রিপোর্ট — সবকিছু"
                    ],
                    [
                        "**Manager**",
                        "ড্যাশবোর্ড, স্ক্রিনশট, রিপোর্ট",
                        "দেখতে পারেন, কিন্তু কর্মী বা সেটিংস বদলাতে পারেন না"
                    ],
                    [
                        "**Employee**",
                        "শুধু নিজের ডেটা",
                        "ডেস্কটপ অ্যাপ চালানো"
                    ]
                ]
            },
            {
                "type": "list",
                "title": "কোন পেজ কী কাজে",
                "items": [
                    "**ড্যাশবোর্ড** — আজ কে কাজ করছে, কে দেরিতে এসেছে, কে শুরুই করেনি।",
                    "**স্ক্রিনশট** — সব কর্মীর স্ক্রিনশট, ঘণ্টা বা ব্যক্তি অনুযায়ী গ্রুপ করা।",
                    "**রিপোর্ট** — উপস্থিতি, টাইমশিট, idle time, বিরতি, টিম ও প্রবণতা বিশ্লেষণ।",
                    "**টিম ও প্রজেক্ট** — কর্মীদের দলে ভাগ করা এবং কাজের সময় প্রজেক্টে ট্যাগ করা।",
                    "**Productivity Rules** — কোন অ্যাপ/সাইট productive, কোনটি নয়।",
                    "**Schedules** — কখন ট্র্যাকিং চালু থাকবে ও বিরতি কখন।"
                ]
            }
        ],
        "icon": "book"
    },
    {
        "key": "organization-setup",
        "title": "প্রতিষ্ঠান সেটআপ",
        "blurb": "রেজিস্ট্রেশন থেকে প্রথম কর্মী যোগ করা পর্যন্ত।",
        "blocks": [
            {
                "type": "steps",
                "items": [
                    {
                        "title": "রেজিস্টার করুন",
                        "text": "প্রতিষ্ঠানের নাম, একটি identifier এবং প্রথম অ্যাডমিন অ্যাকাউন্ট দিন।"
                    },
                    {
                        "title": "identifier মনে রাখুন",
                        "text": "কর্মীরা ডেস্কটপ অ্যাপে এই identifier লিখেই আপনার প্রতিষ্ঠানে যোগ হবেন।"
                    },
                    {
                        "title": "টিম তৈরি করুন",
                        "text": "Engineering, Support ইত্যাদি টিম বানিয়ে প্রতিটিতে একজন ম্যানেজার দিন।"
                    },
                    {
                        "title": "কর্মী যোগ করুন",
                        "text": "Admin ও Manager অ্যাকাউন্ট আপনি বানাবেন; Employee অ্যাকাউন্ট ডেস্কটপ অ্যাপ থেকে নিজে তৈরি হয়।"
                    },
                    {
                        "title": "সময়সূচি ও নিয়ম",
                        "text": "কাজের সময়, বিরতি এবং productivity rule ঠিক করুন — তার পরেই রিপোর্ট অর্থপূর্ণ হবে।"
                    }
                ]
            },
            {
                "type": "note",
                "tone": "warning",
                "title": "identifier পরে বদলানো যায় না",
                "text": "প্রতিটি ডেস্কটপ ইনস্টলেশন এই identifier দিয়েই প্রতিষ্ঠান চেনে। বদলাতে হলে সব ডিভাইসে আবার সাইন ইন করাতে হবে, তাই শুরুতেই ভেবে দিন।"
            },
            {
                "type": "note",
                "tone": "info",
                "title": "স্ক্রিনশট কোথায় জমা হবে",
                "text": "ডিফল্টে আমাদের সার্ভারে। নিজের AWS S3 bucket ব্যবহার করতে চাইলে Settings → Screenshot storage-এ গিয়ে সেট করুন। IAM ইউজারের PutObject, GetObject এবং DeleteObject — তিনটিই দরকার; DeleteObject না থাকলে retention পুরনো ফাইল মুছতে পারবে না।"
            }
        ],
        "icon": "cog"
    },
    {
        "key": "desktop-app-installation",
        "title": "ডেস্কটপ অ্যাপ ইনস্টলেশন",
        "blurb": "Windows ও macOS ইনস্টলেশন, প্রয়োজনীয় অনুমতি এবং যাচাই।",
        "blocks": [
            {
                "type": "steps",
                "items": [
                    {
                        "title": "ডাউনলোড করুন",
                        "text": "প্রতিষ্ঠানের অ্যাডমিনের দেওয়া লিংক থেকে ইনস্টলার নামান। Windows-এ .exe, macOS-এ .dmg।"
                    },
                    {
                        "title": "ইনস্টল করুন",
                        "text": "Windows-এ ইনস্টলার চালান। macOS-এ অ্যাপটি Applications ফোল্ডারে টেনে দিন।"
                    },
                    {
                        "title": "সাইন ইন করুন",
                        "text": "প্রতিষ্ঠানের identifier, তারপর নিজের ইমেইল ও পাসওয়ার্ড দিন।"
                    },
                    {
                        "title": "অনুমতি দিন",
                        "text": "macOS-এ Screen Recording ও Accessibility — দুটিই দিতে হবে, নইলে স্ক্রিনশট বা কীবোর্ড কাউন্ট কাজ করবে না।"
                    },
                    {
                        "title": "যাচাই করুন",
                        "text": "কয়েক মিনিট পর ওয়েব ড্যাশবোর্ডে ওই কর্মীর নাম ও স্ক্রিনশট দেখা যাবে।"
                    }
                ]
            },
            {
                "type": "note",
                "tone": "warning",
                "title": "macOS-এ অনুমতি দুটি আলাদা",
                "text": "System Settings → Privacy & Security → Screen & System Audio Recording এবং Accessibility — দুই জায়গাতেই Time-Tracker চালু করতে হবে। অনুমতি দেওয়ার পর অ্যাপটি একবার বন্ধ করে আবার চালু করুন।"
            },
            {
                "type": "list",
                "title": "ইনস্টলেশনের পর যা দেখে নেবেন",
                "items": [
                    "ড্যাশবোর্ডে কর্মীর নাম “Currently working”-এ আসছে কি না।",
                    "স্ক্রিনশট পেজে ওই কর্মীর প্রথম স্ক্রিনশট এসেছে কি না (৫ মিনিটের মধ্যে)।",
                    "Users পেজে অ্যাপ ভার্সন দেখাচ্ছে কি না — **Latest** ব্যাজ থাকা উচিত।"
                ]
            },
            {
                "type": "note",
                "tone": "danger",
                "title": "পুরনো ভার্সন ইনস্টল করবেন না",
                "text": "2.0-এর আগের build-এ ট্র্যাকিং কিছুক্ষণ পর চুপচাপ বন্ধ হয়ে যাওয়ার সমস্যা ছিল। Users পেজে কার ভার্সন পুরনো তা “Outdated app” পিলে ক্লিক করে দেখে নিন।"
            }
        ],
        "icon": "monitor"
    },
    {
        "key": "employee-monitoring",
        "title": "কর্মচারী মনিটরিং",
        "blurb": "কী কী রেকর্ড হয়, কী হয় না, এবং কর্মীদের কী বলা উচিত।",
        "blocks": [
            {
                "type": "list",
                "title": "যা রেকর্ড হয়",
                "items": [
                    "কাজের সময় — শুরু, শেষ ও মোট সময়।",
                    "সক্রিয় অ্যাপ ও ওয়েবসাইটের নাম এবং কত সময় ব্যবহার হয়েছে।",
                    "কীবোর্ড ও মাউসের _পরিমাণ_ (কতবার), প্রতিটি ইন্টারভালে।",
                    "নিষ্ক্রিয় সময় — কতক্ষণ কোনো ইনপুট ছিল না।",
                    "নির্দিষ্ট বিরতিতে স্ক্রিনশট, প্রতিটি মনিটরের জন্য আলাদা।",
                    "ডিভাইসের তথ্য — নাম, OS, OS ভার্সন, অ্যাপ ভার্সন, টাইমজোন।"
                ]
            },
            {
                "type": "list",
                "title": "যা রেকর্ড হয় না",
                "items": [
                    "**টাইপ করা লেখা নয়** — Key Log চালু থাকলেও শুধু কী-এর _নাম_ জমা হয় (A, SPACE, RETURN), লেখা নয়।",
                    "পাসওয়ার্ড বা ফর্মের ভেতরের ডেটা আলাদাভাবে সংগ্রহ করা হয় না।",
                    "কপি করা ছবি আপলোড হয় না — শুধু “image 1440×900” ধরনের একটি চিহ্ন জমা হয়।",
                    "অ্যাপ বন্ধ থাকলে কিছুই রেকর্ড হয় না।"
                ]
            },
            {
                "type": "note",
                "tone": "warning",
                "title": "Clipboard ক্যাপচার সাবধানে চালু করুন",
                "text": "চালু থাকলে কপি ও পেস্ট করা **লেখা** স্ক্রিনশটের সঙ্গে জমা হয় — এর মধ্যে কেউ password manager থেকে কপি করা পাসওয়ার্ডও পড়তে পারে। প্রয়োজন না হলে বন্ধ রাখুন।"
            },
            {
                "type": "note",
                "tone": "danger",
                "title": "Silent mode ও আইন",
                "text": "Silent mode-এ ডেস্কটপ অ্যাপের কোনো উইন্ডো বা tray আইকন থাকে না, তাই কর্মী দেখতে পান না যে ট্র্যাকিং চলছে। অনেক দেশে গোপনে মনিটরিং সীমাবদ্ধ বা বেআইনি। চালু করার আগে টিমকে জানান এবং স্থানীয় আইন অনুযায়ী সম্মতি নিন।"
            },
            {
                "type": "list",
                "title": "কর্মীদের যা বলা ভালো",
                "items": [
                    "কী কী রেকর্ড হয় এবং কী হয় না।",
                    "স্ক্রিনশট কত বিরতিতে ওঠে এবং কে দেখতে পান।",
                    "ডেটা কত দিন রাখা হয় (Settings → Data retention)।",
                    "কাজের সময়ের বাইরে ট্র্যাকিং বন্ধ থাকে — সময়সূচি অনুযায়ী।"
                ]
            }
        ],
        "icon": "camera"
    },
    {
        "key": "reports-analytics",
        "title": "রিপোর্ট ও বিশ্লেষণ",
        "blurb": "কোন প্রশ্নের উত্তর কোন রিপোর্টে পাবেন।",
        "blocks": [
            {
                "type": "p",
                "text": "সব রিপোর্ট একই ডেটার উপর চলে, শুধু প্রশ্নটা আলাদা। উপরের ডানদিকে সময়সীমা ও টিম বাছাই করে PDF বা Excel-এ এক্সপোর্ট করতে পারেন।"
            },
            {
                "type": "table",
                "columns": [
                    "প্রশ্ন",
                    "রিপোর্ট"
                ],
                "rows": [
                    [
                        "কে সময়মতো আসছে, কে দেরিতে?",
                        "**Attendance Report**"
                    ],
                    [
                        "কে কত ঘণ্টা কাজ করেছে?",
                        "**Timesheet Report**"
                    ],
                    [
                        "সময় কোথায় নষ্ট হচ্ছে?",
                        "**Idle Time Analysis**"
                    ],
                    [
                        "বিরতি কি সময়সূচির সঙ্গে মিলছে?",
                        "**Break Analysis**"
                    ],
                    [
                        "কোন অ্যাপে সবচেয়ে বেশি সময়?",
                        "**Application Usage**"
                    ],
                    [
                        "কোন ওয়েবসাইটে সময় যাচ্ছে?",
                        "**Website Usage**"
                    ],
                    [
                        "কোন টিম ভালো করছে?",
                        "**Team Performance** / **Team Comparison**"
                    ],
                    [
                        "উৎপাদনশীলতা বাড়ছে না কমছে?",
                        "**Productivity Trends**"
                    ],
                    [
                        "এই মুহূর্তে কে কাজ করছে?",
                        "**Real-time Activity**"
                    ]
                ]
            },
            {
                "type": "note",
                "tone": "info",
                "title": "তিনটি সময় আলাদা — গুলিয়ে ফেলবেন না",
                "text": "**Total Tracked Time** = অ্যাপ ও ওয়েবসাইটে মোট সময়। **Total Work Time** তার একটি অংশ। **Idle Time** সম্পূর্ণ আলাদা পাইপলাইনে হিসাব হয় এবং এই দুটোর কোনোটির মধ্যেই ধরা নেই।"
            }
        ],
        "icon": "chart"
    },
    {
        "key": "mobile-usage",
        "title": "মোবাইলে ব্যবহার",
        "blurb": "ফোন থেকে ড্যাশবোর্ড দেখা।",
        "blocks": [
            {
                "type": "p",
                "text": "ওয়েব ড্যাশবোর্ড মোবাইল ব্রাউজারে কাজ করে — সাইডবার ড্রয়ার হয়ে যায় এবং টেবিলগুলো পাশে স্ক্রল করা যায়। ভ্রমণে থাকলে ড্যাশবোর্ড ও রিপোর্ট দেখার জন্য এটিই যথেষ্ট।"
            },
            {
                "type": "note",
                "tone": "info",
                "title": "ট্র্যাকিং শুধু ডেস্কটপে",
                "text": "মোবাইল থেকে কাজের সময় ট্র্যাক করা যায় না — ট্র্যাকিং শুধু Windows, macOS ও Linux ডেস্কটপ অ্যাপে হয়। ফোনে শুধু ড্যাশবোর্ড দেখা যায়।"
            },
            {
                "type": "list",
                "title": "ছোট স্ক্রিনে যা ভালো কাজ করে",
                "items": [
                    "ড্যাশবোর্ডের সংখ্যা-কার্ড এবং “Currently working” তালিকা।",
                    "স্ক্রিনশট গ্রিড — দুই কলামে দেখায়।",
                    "রিপোর্টের সংখ্যা ও চার্ট; বড় টেবিল পাশে স্ক্রল করতে হবে।"
                ]
            }
        ],
        "icon": "smartphone"
    },
    {
        "key": "troubleshooting",
        "title": "সমস্যা সমাধান",
        "blurb": "মাঠে সবচেয়ে বেশি যা ঘটেছে এবং কী করতে হয়।",
        "blocks": [
            {
                "type": "p",
                "text": "নিচের সমস্যাগুলো আসল ব্যবহারকারীদের কাছ থেকে পাওয়া। প্রতিটির সঙ্গে কী করতে হবে দেওয়া আছে।"
            },
            {
                "type": "qa",
                "items": [
                    {
                        "q": "অ্যাপ চালু আছে, কিন্তু ড্যাশবোর্ডে নতুন ডেটা আসছে না",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "প্রথমে **Users** পেজে ওই কর্মীর অ্যাপ ভার্সন দেখুন। 2.0-এর আগের build-এ ট্র্যাকিং চুপচাপ বন্ধ হয়ে যেত — অ্যাপ চলতে থাকত কিন্তু কিছু পাঠাত না।"
                            },
                            {
                                "type": "list",
                                "items": [
                                    "ভার্সন পুরনো হলে সর্বশেষ build ইনস্টল করান — এটাই আসল সমাধান।",
                                    "ভার্সন সর্বশেষ হলে অ্যাপটি একবার বন্ধ করে চালু করান।",
                                    "ইন্টারনেট বন্ধ থাকলে ডেটা ডিভাইসে জমা থাকে এবং সংযোগ ফিরলে নিজেই চলে আসে — এটি সমস্যা নয়।"
                                ]
                            }
                        ]
                    },
                    {
                        "q": "macOS-এ স্ক্রিনশট হঠাৎ বন্ধ হয়ে গেছে",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "macOS মাঝে মাঝে আগে দেওয়া Screen Recording অনুমতি নিজেই আটকে দেয়, বিশেষ করে ঘুম থেকে জাগার পর।"
                            },
                            {
                                "type": "list",
                                "items": [
                                    "System Settings → Privacy & Security → Screen & System Audio Recording-এ Time-Tracker চালু আছে কি না দেখুন।",
                                    "টগলটি একবার বন্ধ করে আবার চালু করুন, তারপর অ্যাপ রিস্টার্ট করুন।",
                                    "Accessibility অনুমতিও একইভাবে দেখে নিন — এটি না থাকলে কীবোর্ড ও মাউস কাউন্ট শূন্য থাকে।"
                                ]
                            }
                        ]
                    },
                    {
                        "q": "মনিটর লাগানো বা খোলার পর স্ক্রিনশট বন্ধ",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "বাইরের মনিটর খুলে ফেললে পুরনো build-এ স্ক্রিনশট সম্পূর্ণ বন্ধ হয়ে যেত। এটি সর্বশেষ ভার্সনে সমাধান করা হয়েছে।"
                            },
                            {
                                "type": "p",
                                "text": "সর্বশেষ build ইনস্টল করান। তার পরেও হলে অ্যাপ রিস্টার্ট করলে সঙ্গে সঙ্গে ঠিক হয়ে যাবে।"
                            }
                        ]
                    },
                    {
                        "q": "একটি entry “Ongoing” অবস্থায় আটকে আছে",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "কম্পিউটার হঠাৎ বন্ধ হলে ওই সময়ের entry শেষ না হয়েই থেকে যেতে পারে। সার্ভারের একটি নির্ধারিত কাজ এমন entry নিজে থেকেই বন্ধ করে দেয় — শেষ সময় হিসেবে _শেষ পাওয়া সিগন্যাল_ ব্যবহার করা হয়, বর্তমান সময় নয়, যাতে অতিরিক্ত ঘণ্টা যোগ না হয়।"
                            },
                            {
                                "type": "p",
                                "text": "কয়েক ঘণ্টা পরেও না ঠিক হলে সাপোর্টে জানান।"
                            }
                        ]
                    },
                    {
                        "q": "কর্মী সময়মতো এসেছে, কিন্তু “Late” দেখাচ্ছে",
                        "blocks": [
                            {
                                "type": "list",
                                "items": [
                                    "**Settings → Dashboard**-এ late start cutoff দেখুন (ডিফল্ট ৯:০৫)।",
                                    "ওই কর্মীর জন্য আলাদা সময়সূচি থাকলে সেটি global সময়সূচিকে override করে — **Schedules → Per user** দেখুন।",
                                    "ডিভাইসের টাইমজোন ভুল থাকলেও এমন হয়। Users পেজে ডিভাইসের টাইমজোন মিলিয়ে নিন।"
                                ]
                            }
                        ]
                    },
                    {
                        "q": "একটি অ্যাপ ভুলভাবে unproductive দেখাচ্ছে",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "**Productivity Rules**-এ গিয়ে ওই অ্যাপ বা ডোমেইনের নিয়মটি খুঁজুন।"
                            },
                            {
                                "type": "list",
                                "items": [
                                    "পুরো প্রতিষ্ঠানের জন্য ভুল হলে global status বদলান।",
                                    "শুধু একটি টিমের জন্য আলাদা হওয়া উচিত হলে **team override** দিন — যেমন Marketing টিমের জন্য YouTube productive।",
                                    "regex ব্যবহার করলে সেভ করার আগে “Test the pattern” বাক্সে যাচাই করে নিন।"
                                ]
                            },
                            {
                                "type": "p",
                                "text": "মনে রাখবেন, আগে রেকর্ড হওয়া ইতিহাস তখনকার status ধরে রাখে — নিয়ম বদলালে পুরনো ডেটা বদলায় না।"
                            }
                        ]
                    },
                    {
                        "q": "কর্মীকে deactivate করেছি, কিন্তু ডেস্কটপ অ্যাপ এখনো চলছে",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "ডেস্কটপ অ্যাপ প্রতি settings sync-এ অবস্থা যাচাই করে, তাই সাধারণত এক মিনিটের মধ্যে নিজেই সাইন আউট হয়ে যায়। সঙ্গে সঙ্গে বন্ধ করতে হলে ডিভাইসে অ্যাপটি বন্ধ করান।"
                            }
                        ]
                    }
                ]
            }
        ],
        "icon": "wrench"
    },
    {
        "key": "faq",
        "title": "সাধারণ প্রশ্ন",
        "blurb": "যা সবচেয়ে বেশি জিজ্ঞাসা করা হয়।",
        "blocks": [
            {
                "type": "qa",
                "items": [
                    {
                        "q": "ইন্টারনেট না থাকলে কী হয়?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "ডেস্কটপ অ্যাপ ডেটা ডিভাইসে জমা রাখে এবং সংযোগ ফিরলে ধীরে ধীরে পাঠিয়ে দেয়। কিছু হারায় না।"
                            }
                        ]
                    },
                    {
                        "q": "কাজের সময়ের পরেও ট্র্যাকিং চলে?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "না। সময়সূচি অনুযায়ী নির্ধারিত সময়ের বাইরে ট্র্যাকিং বন্ধ থাকে। কেউ ইচ্ছে করে চালু রাখলেও সময়সূচি বাইরের সময় কাজের সময় হিসেবে গণ্য হয় না।"
                            }
                        ]
                    },
                    {
                        "q": "একজনের জন্য আলাদা সেটিংস দেওয়া যায়?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "যায়। ওই কর্মীর পেজে **Desktop App Overrides** অংশে স্ক্রিনশট কোয়ালিটি, ইন্টারভাল, tray আইকন, Key Log, clipboard এবং file logging আলাদা করে দেওয়া যায়। কিছু না বদলালে প্রতিষ্ঠানের সেটিংসই চলে।"
                            }
                        ]
                    },
                    {
                        "q": "স্ক্রিনশট কত দিন থাকে?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "Settings → Data retention-এ যত দিন সেট করা আছে (ডিফল্ট ৯০ দিন)। প্রতি রাতে একটি কাজ এর চেয়ে পুরনো স্ক্রিনশট, activity ও time entry স্থায়ীভাবে মুছে দেয় — ফাইলসহ।"
                            }
                        ]
                    },
                    {
                        "q": "কর্মী মুছে দিলে তার স্ক্রিনশটও মুছে যায়?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "হ্যাঁ। কর্মী মুছলে তার সব time entry, idle record, activity এবং স্ক্রিনশট — জমা থাকা ইমেজ ফাইলসহ — মুছে যায়। এটি ফেরানো যায় না। শুধু অ্যাক্সেস বন্ধ করতে চাইলে মুছে না দিয়ে **deactivate** করুন।"
                            }
                        ]
                    },
                    {
                        "q": "একই কর্মী দুটি কম্পিউটারে ব্যবহার করতে পারবে?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "পারবে। দুই ডিভাইসের ডেটা একই অ্যাকাউন্টে যোগ হবে। তবে একই সময়ে দুই জায়গায় কাজ দেখালে ঘণ্টার হিসাব বিভ্রান্তিকর হতে পারে।"
                            }
                        ]
                    },
                    {
                        "q": "একাধিক মনিটর থাকলে?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "প্রতিটি মনিটরের আলাদা স্ক্রিনশট ওঠে এবং কোন উইন্ডো কোন মনিটরে ছিল তাও রাখা হয়।"
                            }
                        ]
                    },
                    {
                        "q": "ম্যানেজার কি সেটিংস বদলাতে পারেন?",
                        "blocks": [
                            {
                                "type": "p",
                                "text": "না। ম্যানেজার ড্যাশবোর্ড, স্ক্রিনশট ও রিপোর্ট দেখতে পারেন, কিন্তু কর্মী, টিম বা প্রতিষ্ঠানের সেটিংস বদলাতে পারেন না — সেটি শুধু অ্যাডমিন করতে পারেন।"
                            }
                        ]
                    }
                ]
            }
        ],
        "icon": "help"
    },
    {
        "key": "support-contact",
        "title": "সাপোর্ট ও যোগাযোগ",
        "blurb": "সমাধান না হলে কোথায় জানাবেন।",
        "blocks": [
            {
                "type": "p",
                "text": "সমস্যা সমাধান অংশে না পেলে আমাদের জানান। নিচের তথ্যগুলো সঙ্গে দিলে অনেক দ্রুত সমাধান হয়।"
            },
            {
                "type": "list",
                "title": "যা সঙ্গে পাঠাবেন",
                "items": [
                    "প্রতিষ্ঠানের identifier।",
                    "কোন কর্মীর ক্ষেত্রে হচ্ছে — নাম ও ইমেইল।",
                    "ডেস্কটপ অ্যাপের ভার্সন এবং OS (Users পেজে দুটোই আছে)।",
                    "কখন থেকে শুরু হয়েছে — তারিখ ও সময়।",
                    "ড্যাশবোর্ডে কী দেখছেন তার একটি স্ক্রিনশট।"
                ]
            },
            {
                "type": "note",
                "tone": "info",
                "title": "ডায়াগনস্টিক লগ",
                "text": "কঠিন সমস্যার ক্ষেত্রে সাপোর্ট টিম **File Logging** চালু করতে বলতে পারে (ওই কর্মীর পেজে Desktop App Overrides-এ)। এতে ডিভাইসে একটি লগ ফাইল তৈরি হয়, যা tray মেনুর “Open Logs Folder” থেকে পাওয়া যায়। লগে কোনো টাইপ করা লেখা বা clipboard-এর বিষয়বস্তু থাকে না। কাজ শেষে আবার বন্ধ করে দিন।"
            },
            {
                "type": "table",
                "columns": [
                    "মাধ্যম",
                    "কীসের জন্য",
                    "কোথায়"
                ],
                "rows": [
                    [
                        "ইমেইল",
                        "সাধারণ সহায়তা",
                        "`support@hastechit.com`"
                    ],
                    [
                        "ফোন",
                        "জরুরি সমস্যা",
                        "`+880 1711 000111`"
                    ],
                    [
                        "প্রতিষ্ঠানের অ্যাডমিন",
                        "অ্যাকাউন্ট, পাসওয়ার্ড, অনুমতি",
                        "আপনার নিজের প্রতিষ্ঠানের অ্যাডমিন"
                    ]
                ]
            }
        ],
        "icon": "mail"
    }
];

/** Everything a topic contains, flattened, so search can look inside blocks. */
export function topicText(topic) {
    const parts = [topic.title, topic.blurb];

    const eat = (blocks) => {
        for (const b of blocks ?? []) {
            if (b.title) parts.push(b.title);
            if (b.text) parts.push(b.text);
            if (b.items) {
                for (const item of b.items) {
                    if (typeof item === "string") parts.push(item);
                    else {
                        if (item.q) parts.push(item.q);
                        if (item.title) parts.push(item.title);
                        if (item.text) parts.push(item.text);
                        eat(item.blocks);
                    }
                }
            }
            if (b.rows) for (const row of b.rows) parts.push(row.join(" "));
            if (b.columns) parts.push(b.columns.join(" "));
        }
    };

    eat(topic.blocks);
    return parts.join(" ").toLowerCase();
}
